/**
 * AuthContext — Phase 1
 * 
 * Provides real JWT-based authentication context to the entire app.
 * Stores token in localStorage, validates on mount, exposes login/register/logout.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const STRATEGY_ENGINE_URL =
  process.env.REACT_APP_STRATEGY_ENGINE_URL ||
  "https://strategy-engine.houman-ghavamzadeh.workers.dev";

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Helper: get token from either storage
const getStoredToken = () => localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
const getStoredUserId = () => localStorage.getItem('user_id') || sessionStorage.getItem('user_id');

// Helper: persist auth data to the appropriate storage
const persistAuth = (data, remember) => {
  const storage = remember ? localStorage : sessionStorage;
  storage.setItem('auth_token', data.token);
  storage.setItem('user_id', data.user.id);
  storage.setItem('userRole', data.user.role);
  storage.setItem('isAuthenticated', 'true');
  // Always keep user_id in localStorage for StrategyEngineService compatibility
  localStorage.setItem('user_id', data.user.id);
  localStorage.setItem('auth_token', data.token);
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => getStoredToken());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Validate token on mount
  useEffect(() => {
    if (token) {
      validateToken(token);
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateToken = async (t) => {
    try {
      const response = await fetch(`${STRATEGY_ENGINE_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${t}` },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data?.user) {
          setUser(data.data.user);
          setToken(t);
          localStorage.setItem('auth_token', t);
          // Also set user_id for backward compat with StrategyEngineService
          localStorage.setItem('user_id', data.data.user.id);
          localStorage.setItem('userRole', data.data.user.role);
          localStorage.setItem('isAuthenticated', 'true');
        } else {
          clearAuth();
        }
      } else {
        clearAuth();
      }
    } catch (err) {
      console.error('[AuthContext] Token validation failed:', err);
      clearAuth();
    } finally {
      setLoading(false);
    }
  };

  const clearAuth = useCallback(() => {
    setUser(null);
    setToken(null);
    // Clear both storages
    for (const storage of [localStorage, sessionStorage]) {
      storage.removeItem('auth_token');
      storage.removeItem('user_id');
      storage.removeItem('userRole');
      storage.removeItem('isAuthenticated');
    }
  }, []);

  const login = async (email, password, rememberMe = true) => {
    setError(null);
    try {
      const response = await fetch(`${STRATEGY_ENGINE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (data.success && data.data) {
        setUser(data.data.user);
        setToken(data.data.token);
        persistAuth({ token: data.data.token, user: data.data.user }, rememberMe);
        return { success: true, user: data.data.user };
      } else {
        const msg = data.error || 'Login failed';
        setError(msg);
        return { success: false, error: msg };
      }
    } catch (err) {
      const msg = err.message || 'Network error';
      setError(msg);
      return { success: false, error: msg };
    }
  };

  const register = async (email, password, displayName) => {
    setError(null);
    try {
      const response = await fetch(`${STRATEGY_ENGINE_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, displayName }),
      });
      const data = await response.json();
      if (data.success && data.data) {
        setUser(data.data.user);
        setToken(data.data.token);
        localStorage.setItem('auth_token', data.data.token);
        localStorage.setItem('user_id', data.data.user.id);
        localStorage.setItem('userRole', data.data.user.role);
        localStorage.setItem('isAuthenticated', 'true');
        return { success: true, user: data.data.user };
      } else {
        const msg = data.error || 'Registration failed';
        setError(msg);
        return { success: false, error: msg };
      }
    } catch (err) {
      const msg = err.message || 'Network error';
      setError(msg);
      return { success: false, error: msg };
    }
  };

  const logout = useCallback(() => {
    clearAuth();
  }, [clearAuth]);

  const isAuthenticated = !!user && !!token;
  const isAdmin = user?.role === 'admin';

  const value = {
    user,
    token,
    loading,
    error,
    isAuthenticated,
    isAdmin,
    login,
    register,
    logout,
    clearError: () => setError(null),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;
